/**
 * Evaluación GPS en el dispositivo.
 *
 * La detección autoritativa la hace el servidor con PostGIS al sincronizar.
 * Esta copia local existe porque el encuestador necesita saber EN LA PUERTA si
 * está en el área correcta, no horas después: si la cartografía no coincide,
 * tiene que justificarlo en el momento, y si se equivocó de manzana, tiene que
 * enterarse antes de los 20 minutos de entrevista.
 *
 * Usa las MISMAS funciones geométricas que el servidor en su respaldo sin
 * PostGIS (`pointInMultiPolygon`, `distanceToPolygonMeters` de @capi/shared),
 * de modo que el veredicto local y el del servidor no puedan divergir por
 * tener dos implementaciones distintas.
 */

import {
  QUALITY_THRESHOLDS,
  distanceToPolygonMeters,
  pointInMultiPolygon,
  type Coordinate,
  type GpsDetectionResult,
  type GpsDetectionStatus,
  type PolygonRings,
} from '@capi/shared';

import { CACHE_KEYS, getCache, putCache } from '../db/db';
import { api, type GeoJsonFeatureCollection } from '../api/client';

/* -------------------------------------------------------------------------- */
/*                         Geometrías en el dispositivo                       */
/* -------------------------------------------------------------------------- */

interface CachedGeometry {
  /** Sección de la cartografía vigente. */
  seccion: number;
  /** Sección del marco a la que corresponde (722 para las 2216-2222). */
  frameSeccion: number;
  viaEquivalence: boolean;
  polygons: PolygonRings[];
}

function toPolygons(geometry: {
  type: 'Polygon' | 'MultiPolygon';
  coordinates: unknown;
}): PolygonRings[] {
  const raw =
    geometry.type === 'Polygon'
      ? [geometry.coordinates as number[][][]]
      : (geometry.coordinates as number[][][][]);
  return raw.map((polygon) =>
    polygon.map((ring) => ring.map(([lon, lat]) => [lon!, lat!] as const)),
  );
}

function parseCollection(
  collection: GeoJsonFeatureCollection,
  fallbackFrameSeccion: number,
): CachedGeometry[] {
  return collection.features.map((feature) => {
    const seccion = Number(feature.properties['seccion'] ?? fallbackFrameSeccion);
    const frameSeccion = Number(feature.properties['frameSeccion'] ?? fallbackFrameSeccion);
    return {
      seccion,
      frameSeccion: Number.isFinite(frameSeccion) ? frameSeccion : fallbackFrameSeccion,
      viaEquivalence: Boolean(feature.properties['viaEquivalence']),
      polygons: toPolygons(feature.geometry),
    };
  });
}

/**
 * Descarga y cachea la geometría de una sección del marco.
 *
 * Para la sección 722 el servidor devuelve las SIETE secciones (2216-2222) que
 * la sustituyeron tras el reseccionamiento, todas marcadas con
 * `frameSeccion: 722`. Así la evaluación local reconoce la 722 exactamente
 * igual que el servidor.
 */
export async function prefetchSectionGeometry(frameSeccion: number): Promise<CachedGeometry[]> {
  const collection = await api.sectionGeoJson(frameSeccion);
  const parsed = parseCollection(collection, frameSeccion);
  await putCache(CACHE_KEYS.geoSection(frameSeccion), collection);
  return parsed;
}

export async function prefetchClusterGeometry(
  clusterId: string,
): Promise<GeoJsonFeatureCollection> {
  const collection = await api.clusterGeoJson(clusterId);
  await putCache(CACHE_KEYS.geoCluster(clusterId), collection);
  return collection;
}

async function loadSectionGeometry(frameSeccion: number): Promise<CachedGeometry[] | null> {
  const cached = await getCache<GeoJsonFeatureCollection>(CACHE_KEYS.geoSection(frameSeccion));
  if (cached) return parseCollection(cached.value, frameSeccion);
  if (!navigator.onLine) return null;
  try {
    return await prefetchSectionGeometry(frameSeccion);
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/*                            Detección local                                */
/* -------------------------------------------------------------------------- */

/**
 * Evalúa la coordenada contra la geometría cacheada de la sección esperada.
 *
 * Devuelve `null` cuando no hay geometría en el dispositivo: en ese caso la PWA
 * no puede emitir un veredicto y debe decirlo, en lugar de inventar uno. Marcar
 * una captura como válida sin haber comprobado nada sería peor que no comprobar.
 */
export async function detectOffline(
  coordinate: Coordinate | null,
  expectedFrameSeccion: number | null,
): Promise<GpsDetectionResult | null> {
  if (!coordinate || !Number.isFinite(coordinate.latitude) || !Number.isFinite(coordinate.longitude)) {
    return {
      status: 'unavailable',
      detectedSeccion: null,
      detectedFrameSeccion: null,
      expectedFrameSeccion,
      detectedClusterId: null,
      detectedManzanaId: null,
    // El respaldo sin conexión sólo lleva polígonos de SECCIÓN en el
    // dispositivo: las 3,256 manzanas y las 133 colonias pesan demasiado
    // para el paquete de campo, que viaja por los datos del encuestador.
    // Se resuelven al sincronizar, contra PostGIS.
    detectedManzanaCvegeo: null,
    detectedColonia: null,
      distanceToExpectedMeters: null,
      valid: false,
      requiresReason: true,
      message:
        'No se obtuvo coordenada GPS. §9.4 exige GPS en cada captura: sal al aire libre y reintenta.',
      evaluatedOffline: true,
    };
  }

  if (expectedFrameSeccion === null) return null;

  const geometries = await loadSectionGeometry(expectedFrameSeccion);
  if (!geometries || geometries.length === 0) return null;

  // ¿Cae dentro de alguna de las geometrías que cubren la sección esperada?
  const containing = geometries.find((g) => pointInMultiPolygon(coordinate, g.polygons));

  if (containing) {
    const status: GpsDetectionStatus = containing.viaEquivalence
      ? 'inside-expected-by-equivalence'
      : 'inside-expected';
    return {
      status,
      detectedSeccion: containing.seccion,
      detectedFrameSeccion: containing.frameSeccion,
      expectedFrameSeccion,
      detectedClusterId: null,
      detectedManzanaId: null,
    // El respaldo sin conexión sólo lleva polígonos de SECCIÓN en el
    // dispositivo: las 3,256 manzanas y las 133 colonias pesan demasiado
    // para el paquete de campo, que viaja por los datos del encuestador.
    // Se resuelven al sincronizar, contra PostGIS.
    detectedManzanaCvegeo: null,
    detectedColonia: null,
      distanceToExpectedMeters: 0,
      valid: true,
      requiresReason: false,
      message: containing.viaEquivalence
        ? `Ubicación confirmada en la sección ${expectedFrameSeccion} del marco, detectada como sección ${containing.seccion} en la cartografía vigente.`
        : `Ubicación confirmada en la sección ${expectedFrameSeccion}.`,
      evaluatedOffline: true,
    };
  }

  // Fuera: la distancia al borde distingue una vivienda en el límite de una
  // captura en otra colonia.
  let minDistance = Infinity;
  for (const geometry of geometries) {
    for (const polygon of geometry.polygons) {
      const d = distanceToPolygonMeters(coordinate, polygon);
      if (d < minDistance) minDistance = d;
    }
  }
  const distance = Number.isFinite(minDistance) ? Math.round(minDistance * 100) / 100 : null;

  if (distance !== null && distance <= QUALITY_THRESHOLDS.polygonBufferMeters) {
    return {
      status: 'inside-buffer',
      detectedSeccion: null,
      detectedFrameSeccion: null,
      expectedFrameSeccion,
      detectedClusterId: null,
      detectedManzanaId: null,
    // El respaldo sin conexión sólo lleva polígonos de SECCIÓN en el
    // dispositivo: las 3,256 manzanas y las 133 colonias pesan demasiado
    // para el paquete de campo, que viaja por los datos del encuestador.
    // Se resuelven al sincronizar, contra PostGIS.
    detectedManzanaCvegeo: null,
    detectedColonia: null,
      distanceToExpectedMeters: distance,
      valid: true,
      requiresReason: false,
      message: `La vivienda está a ${Math.round(distance)} m del borde de la sección ${expectedFrameSeccion}. Se acepta como límite de sección.`,
      evaluatedOffline: true,
    };
  }

  const accuracy = coordinate.accuracyMeters ?? null;
  if (accuracy !== null && accuracy > QUALITY_THRESHOLDS.maxGpsAccuracyMeters) {
    return {
      status: 'low-accuracy',
      detectedSeccion: null,
      detectedFrameSeccion: null,
      expectedFrameSeccion,
      detectedClusterId: null,
      detectedManzanaId: null,
    // El respaldo sin conexión sólo lleva polígonos de SECCIÓN en el
    // dispositivo: las 3,256 manzanas y las 133 colonias pesan demasiado
    // para el paquete de campo, que viaja por los datos del encuestador.
    // Se resuelven al sincronizar, contra PostGIS.
    detectedManzanaCvegeo: null,
    detectedColonia: null,
      distanceToExpectedMeters: distance,
      valid: false,
      requiresReason: false,
      message: `La precisión del GPS es de ±${Math.round(accuracy)} m, por encima del máximo de ±${QUALITY_THRESHOLDS.maxGpsAccuracyMeters} m. Sal al aire libre y vuelve a tomar la lectura.`,
      evaluatedOffline: true,
    };
  }

  return {
    // Sin la cartografía completa en el dispositivo no se puede saber EN QUÉ
    // otra sección cayó, sólo que no está en la esperada. Se reporta el estado
    // menos específico y el servidor lo precisa al sincronizar.
    status: 'outside-all-sections',
    detectedSeccion: null,
    detectedFrameSeccion: null,
    expectedFrameSeccion,
    detectedClusterId: null,
    detectedManzanaId: null,
    // El respaldo sin conexión sólo lleva polígonos de SECCIÓN en el
    // dispositivo: las 3,256 manzanas y las 133 colonias pesan demasiado
    // para el paquete de campo, que viaja por los datos del encuestador.
    // Se resuelven al sincronizar, contra PostGIS.
    detectedManzanaCvegeo: null,
    detectedColonia: null,
    distanceToExpectedMeters: distance,
    valid: false,
    requiresReason: true,
    message: `La ubicación está a ${distance === null ? 'una distancia desconocida' : `${Math.round(distance)} m`} de la sección ${expectedFrameSeccion} que tienes asignada. Indica el motivo para continuar.`,
    evaluatedOffline: true,
  };
}

/**
 * Detección con servidor y respaldo local.
 *
 * Se intenta primero el servidor porque tiene la cartografía completa y puede
 * decir en qué sección cayó la coordenada, no sólo que no está en la esperada.
 * Si no hay red, se usa la evaluación local.
 */
export async function detectWithFallback(
  coordinate: Coordinate | null,
  expectedFrameSeccion: number | null,
  expectedClusterId: string | null,
): Promise<GpsDetectionResult | null> {
  if (navigator.onLine && coordinate) {
    try {
      return await api.detectGps({
        latitude: coordinate.latitude,
        longitude: coordinate.longitude,
        accuracyMeters: coordinate.accuracyMeters ?? null,
        expectedFrameSeccion,
        expectedClusterId,
      });
    } catch {
      // Sin ruido: la caída al modo local es el comportamiento esperado en
      // campo, no una excepción.
    }
  }
  return detectOffline(coordinate, expectedFrameSeccion);
}

/* -------------------------------------------------------------------------- */
/*                        Precarga del área asignada                          */
/* -------------------------------------------------------------------------- */

export interface PrefetchReport {
  sections: number[];
  clusters: string[];
  failed: { target: string; reason: string }[];
}

/**
 * Precarga la cartografía de los conglomerados asignados.
 *
 * Se llama al iniciar sesión con WiFi: es el momento en que el encuestador
 * tiene red y batería, y el único en que puede garantizarse que llevará los
 * polígonos al campo.
 */
export async function prefetchAssignedGeometry(
  assignments: readonly { cluster: { id: string; seccion: number } }[],
): Promise<PrefetchReport> {
  const report: PrefetchReport = { sections: [], clusters: [], failed: [] };

  const sections = [...new Set(assignments.map((a) => a.cluster.seccion))];
  for (const seccion of sections) {
    try {
      await prefetchSectionGeometry(seccion);
      report.sections.push(seccion);
    } catch (error) {
      report.failed.push({
        target: `sección ${seccion}`,
        reason: error instanceof Error ? error.message : 'error desconocido',
      });
    }
  }

  for (const assignment of assignments) {
    try {
      await prefetchClusterGeometry(assignment.cluster.id);
      report.clusters.push(assignment.cluster.id);
    } catch (error) {
      report.failed.push({
        target: `conglomerado ${assignment.cluster.id}`,
        reason: error instanceof Error ? error.message : 'error desconocido',
      });
    }
  }

  return report;
}
